import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, ArrowRight } from 'lucide-react';

const services = [
  {
    id: 'google-ads',
    slug: '/services/google-ads',
    name: 'Google Ads',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/c/c7/Google_Ads_logo.svg',
    borderColor: '#34A853', // GREEN
    description: 'Expert campaign strategy and optimization to maximize your Return on Ad Spend.',
    features: [
      'Campaign strategy & setup',
      'Keyword research & targeting',
      'Bid management & optimization',
      'Conversion tracking',
      'Performance reporting',
      'Budget optimization',
    ],
  },
  {
    id: 'gtm',
    slug: '/services/google-tag-manager',
    name: 'Google Tag Manager',
    logo: 'https://www.gstatic.com/analytics-suite/header/suite/v2/ic_tag_manager.svg',
    borderColor: '#4285F4', // BLUE
    description: 'Robust tracking implementation for accurate data collection and GDPR compliance.',
    features: [
      'Container setup & migration',
      'Tag configuration',
      'Data layer implementation',
      'Event tracking setup',
      'GDPR compliance',
      'Quality assurance & testing',
    ],
  },
  {
    id: 'analytics',
    slug: '/services/google-analytics',
    name: 'Google Analytics',
    logo: 'https://www.gstatic.com/analytics-suite/header/suite/v2/ic_analytics.svg',
    borderColor: '#FBBC05', // YELLOW
    description: 'Complete GA4 setup with custom reporting and actionable insights.',
    features: [
      'GA4 setup & configuration',
      'Event tracking implementation',
      'Conversion setup',
      'Custom reporting',
      'Data analysis & insights',
      'Goal tracking',
    ],
  },
];

export const Services = () => {
  return (
    <section id="services" className="py-20 lg:py-28 bg-secondary/30 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#34A853]/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Our <span className="text-[#4285F4]">Services</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Expert implementation and optimization across Google's ecosystem
          </p>
        </div>

        {/* Service Cards Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service) => (
            <Card 
              key={service.id} 
              className="bg-card border-border/50 shadow-card hover:shadow-card-hover transition-all duration-300 service-card group"
              style={{ borderTop: `4px solid ${service.borderColor}` }}
            >
              <CardContent className="p-6 flex flex-col h-full">
                {/* Logo */}
                <div className="mb-5">
                  <img 
                    src={service.logo}
                    alt={`${service.name} service logo`}
                    width="56"
                    height="56"
                    loading="lazy"
                    className="w-14 h-14"
                  />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">{service.name}</h3>
                <p className="text-sm text-muted-foreground mb-5">{service.description}</p>
                <ul className="space-y-2 mb-6 flex-1">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-[#34A853] mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link 
                  to={service.slug} 
                  className="mt-auto"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <Button variant="ghost" className="w-full justify-between group/btn hover:bg-secondary">
                    Learn More
                    <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
